import { type SelectChangeEvent, InputAdornment, MenuItem, Select, TextField as MuiTextField } from "@mui/material"
import { useFormikContext } from "formik"
import { ChevronDown, Phone } from "lucide-mui"
import React from "react"

import { type ReservationFormValues } from "@/pages/public/venue"

type PhoneCountry = {
  code: string
  dialCode: string
  name: string
}

const PHONE_COUNTRIES: PhoneCountry[] = [
  { code: "PL", dialCode: "+48", name: "Poland" },
  { code: "DE", dialCode: "+49", name: "Germany" },
  { code: "FR", dialCode: "+33", name: "France" },
  { code: "GB", dialCode: "+44", name: "United Kingdom" },
  { code: "IT", dialCode: "+39", name: "Italy" },
  { code: "ES", dialCode: "+34", name: "Spain" },
  { code: "US", dialCode: "+1", name: "United States" },
]

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    bgcolor: "#ffffff",
    minHeight: "42px",
    "& fieldset": { borderColor: "#f1f2f5" },
    "&:hover fieldset": { borderColor: "#dfe3e8" },
    "&.Mui-focused fieldset": { borderColor: "#2596fa" },
  },
  "& .MuiOutlinedInput-input": {
    py: "8px",
    pl: 0,
    fontSize: "12px",
    lineHeight: "18px",
    color: "#808080",
  },
  "& .MuiInputAdornment-root": {
    maxHeight: "none",
  },
} as const

const findCountry = (countryCode?: string | null) => {
  const normalizedCode = countryCode?.toUpperCase()

  return PHONE_COUNTRIES.find((country) => country.code === normalizedCode) || PHONE_COUNTRIES[0]
}

type PhoneInputProps = {
  name: "phone"
  defaultCountry?: string | null
  mobileSheet?: boolean
}

const PhoneInput: React.FC<PhoneInputProps> = ({ name, defaultCountry, mobileSheet = false }) => {
  const { values, touched, errors, handleBlur, setFieldTouched, setFieldValue } =
    useFormikContext<ReservationFormValues>()
  const fieldValue = values[name] || ""
  const [selectedCountryCode, setSelectedCountryCode] = React.useState(findCountry(defaultCountry).code)

  React.useEffect(() => {
    const matchedCountry = PHONE_COUNTRIES.find((country) => fieldValue.startsWith(country.dialCode))

    if (matchedCountry) {
      setSelectedCountryCode(matchedCountry.code)
      return
    }

    setSelectedCountryCode(findCountry(defaultCountry).code)
  }, [defaultCountry, fieldValue])

  const selectedCountry = PHONE_COUNTRIES.find((country) => country.code === selectedCountryCode) || PHONE_COUNTRIES[0]
  const localNumber = fieldValue.startsWith(selectedCountry.dialCode)
    ? fieldValue.slice(selectedCountry.dialCode.length).trimStart()
    : fieldValue
  const inputSx = mobileSheet
    ? {
        "& .MuiOutlinedInput-root": {
          borderRadius: "5px",
          bgcolor: "#ffffff",
          minHeight: "40px",
          "& fieldset": { borderColor: "#f1f2f5" },
          "&:hover fieldset": { borderColor: "#dfe3e8" },
          "&.Mui-focused fieldset": { borderColor: "#2596fa" },
        },
        "& .MuiOutlinedInput-input": {
          py: "9px",
          pl: 0,
          fontSize: "14px",
          lineHeight: "20px",
          color: "#374151",
        },
        "& .MuiInputAdornment-root": {
          maxHeight: "none",
        },
      }
    : INPUT_SX

  const handleCountryChange = (event: SelectChangeEvent<string>) => {
    const nextCountry = PHONE_COUNTRIES.find((country) => country.code === event.target.value) || PHONE_COUNTRIES[0]

    setSelectedCountryCode(nextCountry.code)
    void setFieldValue(name, localNumber ? `${nextCountry.dialCode} ${localNumber}` : "")
  }

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextLocalNumber = event.target.value.replace(/^\s+/, "")

    void setFieldValue(name, nextLocalNumber ? `${selectedCountry.dialCode} ${nextLocalNumber}` : "")
  }

  return (
    <MuiTextField
      id={name}
      name={name}
      fullWidth
      value={localNumber}
      onChange={handleNumberChange}
      onBlur={handleBlur}
      error={Boolean(touched[name] && errors[name])}
      helperText={touched[name] ? errors[name] : undefined}
      data-testid="public-reservation-modal-guest-phone"
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start" sx={{ ml: 0.5, mr: 0.25 }}>
              <div className={`flex items-center gap-1.5 ${mobileSheet ? "text-[#acb3c0]" : "text-[#808694]"}`}>
                <Phone sx={{ fontSize: 14 }} />
                <Select
                  value={selectedCountry.code}
                  variant="standard"
                  disableUnderline
                  onChange={handleCountryChange}
                  onBlur={() => setFieldTouched(name, true, false)}
                  IconComponent={ChevronDown}
                  renderValue={(value) => value}
                  inputProps={{ "data-testid": "public-reservation-modal-guest-phone-country" }}
                  sx={{
                    minWidth: "40px",
                    "& .MuiSelect-select": {
                      p: 0,
                      pr: "18px !important",
                      fontSize: "12px",
                      fontWeight: 500,
                      lineHeight: "18px",
                      color: mobileSheet ? "#6f7484" : "#414142",
                    },
                    "& .MuiSelect-icon": {
                      right: "-2px",
                      color: mobileSheet ? "#acb3c0" : "#808694",
                      fontSize: "14px",
                    },
                  }}
                >
                  {PHONE_COUNTRIES.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
                <span className={`text-[12px] leading-[18px] ${mobileSheet ? "text-[#374151]" : "text-[#808080]"}`}>
                  {selectedCountry.dialCode}
                </span>
              </div>
            </InputAdornment>
          ),
        },
      }}
      sx={inputSx}
    />
  )
}

export default PhoneInput
